import { useState, useCallback } from 'react'
import { db } from '../lib/db'

function debounce(fn, ms) {
  let t
  return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms) }
}

// T3: does this query look like a phone number rather than a name? Digit-ish
// (3+ digits, no letters) — mirrors the server's phone-search guard so the UI
// can label the empty state ("no player with that number") and always fire the
// lookup for short numeric queries. Phone chars ((), -, +, spaces) are allowed.
export function looksLikePhone(query) {
  const digits = String(query || '').replace(/\D/g, '')
  return digits.length >= 3 && !/[a-z]/i.test(query)
}

// Extract a numeric handicap from a search_players row. Prefer attested >
// overall > starter, matching resolvePlayerHandicap in lib/golf.js.
function knownHandicap(p) {
  const raw = p?.tf_index_attested ?? p?.tf_index_overall ?? p?.starter_handicap_index
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export default function PlayerSearchInput({ index, value, playerId, onChange, footer }) {
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [showHcpInput, setShowHcpInput] = useState(false)
  const isLinked = !!value.linked_player_id
  // The linked player already has a handicap on file — no need to prompt.
  const linkedHasHcp = isLinked && value.linked_handicap != null

  const search = useCallback(
    debounce(async (query) => {
      if (query.length < 2 || !playerId) { setResults([]); return }
      setSearching(true)
      try {
        const { data } = await db('search_players', { query, player_id: playerId })
        setResults(data || [])
      } catch { setResults([]) }
      setSearching(false)
    }, 300),
    [playerId]
  )

  const onNameChange = (name) => {
    onChange({ name, phone: '', linked_player_id: null, handicap: null, linked_handicap: null, linked_birthdate: null })
    search(name)
    setShowHcpInput(false)
  }

  const pickPlayer = (p) => {
    const hcp = knownHandicap(p)
    onChange({
      name: p.name,
      phone: '',
      linked_player_id: p.id,
      handicap: null,
      linked_handicap: hcp,
      // F-100-T1 review fix: forward the linked player's birthdate so
      // PlayPage's seatBirthdates prop can feed the pre-round wager
      // under-18 rejection. search_players (netlify/functions/db.js)
      // returns birthdate for exactly this purpose. Null when the linked
      // player never entered one on signup — the server gate still
      // catches them at create_game time.
      linked_birthdate: p.birthdate ?? null,
    })
    setResults([])
    setShowHcpInput(false)
  }

  const clearLinked = () => {
    onChange({ name: '', phone: '', linked_player_id: null, handicap: null, linked_handicap: null, linked_birthdate: null })
    setResults([])
    setShowHcpInput(false)
  }

  const onHcpChange = (raw) => {
    // Accept digits + one decimal; store as number or null.
    const cleaned = raw.replace(/[^0-9.]/g, '')
    const n = cleaned === '' ? null : Number(cleaned)
    onChange({ ...value, handicap: n === null || !Number.isFinite(n) ? null : n })
  }

  return (
    <div style={{
      padding: '12px 14px', marginBottom: 8,
      background: 'var(--green-dark)', borderRadius: 'var(--radius)',
      border: `1px solid ${isLinked ? 'var(--gold)' : 'var(--green-mid)'}`,
    }}>
      <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: 6, fontWeight: 500 }}>
        Player {index + 2}
      </p>

      {isLinked ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          padding: '8px 10px', background: 'rgba(212,185,122,0.08)',
          borderRadius: 'var(--radius)', border: '1px solid var(--gold)',
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.88rem', flex: 1, minWidth: 0 }}>{value.name}</span>
          {linkedHasHcp && (
            <span style={{ fontSize: '0.68rem', color: 'var(--gray-300)', fontFamily: 'var(--font-mono)' }}>
              HCP {value.linked_handicap}
            </span>
          )}
          {!linkedHasHcp && !showHcpInput && (
            <button
              type="button"
              onClick={() => setShowHcpInput(true)}
              style={{
                background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer',
                padding: 0, fontSize: '0.68rem', fontWeight: 600, lineHeight: 1.15,
                textAlign: 'right', flexShrink: 0,
              }}
            >
              Add<br/>Handicap
            </button>
          )}
          {showHcpInput && !linkedHasHcp && (
            <input
              value={value.handicap == null ? '' : String(value.handicap)}
              onChange={e => onHcpChange(e.target.value)}
              placeholder="HCP"
              inputMode="numeric"
              type="text"
              maxLength={2}
              autoFocus
              style={{
                width: 48, padding: '4px 6px', textAlign: 'center',
                background: 'var(--green-deep)', color: 'var(--cream)',
                border: '1px solid var(--gold)', borderRadius: 6,
                fontFamily: 'var(--font-mono)', fontSize: '0.8rem', fontWeight: 600,
                flexShrink: 0,
              }}
            />
          )}
          <button
            onClick={clearLinked}
            style={{ background: 'none', border: 'none', color: 'var(--gray-500)', cursor: 'pointer', fontSize: '0.8rem' }}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              className="input"
              value={value.name}
              onChange={e => onNameChange(e.target.value)}
              placeholder="Search name or phone..."
              autoComplete="off"
              style={{ flex: 1 }}
            />
            {value.name && showHcpInput && (
              <input
                value={value.handicap == null ? '' : String(value.handicap)}
                onChange={e => onHcpChange(e.target.value)}
                placeholder="HCP"
                inputMode="numeric"
                type="text"
                maxLength={2}
                autoFocus
                style={{
                  width: 48, padding: '6px 6px', textAlign: 'center',
                  background: 'var(--green-deep)', color: 'var(--cream)',
                  border: '1px solid var(--gold)', borderRadius: 6,
                  fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 600,
                  flexShrink: 0,
                }}
              />
            )}
            {value.name && !showHcpInput && value.handicap == null && (
              <button
                type="button"
                onClick={() => setShowHcpInput(true)}
                style={{
                  background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer',
                  padding: '0 4px', fontSize: '0.68rem', fontWeight: 600, lineHeight: 1.15,
                  textAlign: 'right', flexShrink: 0,
                }}
              >
                Add<br/>Handicap
              </button>
            )}
          </div>

          {searching && <p className="text-xs text-muted" style={{ marginTop: 4 }}>Searching...</p>}

          {results.length > 0 && (
            <div style={{
              marginTop: 4, borderRadius: 'var(--radius)',
              border: '1px solid var(--green-mid)', overflow: 'hidden',
            }}>
              {results.map(p => (
                <button
                  key={p.id}
                  onClick={() => pickPlayer(p)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', textAlign: 'left',
                    padding: '10px 12px', background: 'var(--green-dark)',
                    border: 'none', borderBottom: '1px solid var(--green-mid)',
                    color: 'var(--cream)', cursor: 'pointer', fontSize: '0.85rem',
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{p.name}</span>
                  {p.is_friend && (
                    <span style={{ fontSize: '0.72rem', color: 'var(--gold)', fontWeight: 600 }}>(friend)</span>
                  )}
                  {!p.auth_user_id && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--gray-500)' }}>· no account</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {value.name && value.name.length >= 2 && !searching && results.length === 0 && !isLinked && (
            <p className="text-xs text-muted" style={{ marginTop: 4 }}>
              {looksLikePhone(value.name)
                ? 'No player found with that number — will be added as a new player'
                : 'No matches — will be added as a new player'}
            </p>
          )}

          {value.name && !isLinked && (
            <input
              className="input"
              value={value.phone}
              onChange={e => onChange({ ...value, phone: e.target.value })}
              placeholder="Phone (optional)"
              type="tel"
              style={{ marginTop: 8 }}
            />
          )}
        </>
      )}
      {footer}
    </div>
  )
}
